/**
 * Data layer for `pre-landing-page-support`. Thin typed fetch wrappers over /api/v1/pre-landing-page-support.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PreLandingPageSupportRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPreLandingPageSupportResponse {
	count: number;
	items: PreLandingPageSupportRow[];
}

export interface CreatePreLandingPageSupportBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/pre-landing-page-support";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPreLandingPageSupportResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPreLandingPageSupportResponse>(res);
}

export async function create(body: CreatePreLandingPageSupportBody): Promise<PreLandingPageSupportRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PreLandingPageSupportRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const preLandingPageSupportQueryKeys = {
	all: ["pre-landing-page-support"] as const,
	list: () => [...preLandingPageSupportQueryKeys.all] as const,
	detail: (id: string) => [...preLandingPageSupportQueryKeys.all, id] as const,
};
